// l18n
import { TFunction } from 'i18next';

// types
import {
	Entity,
	ExpenseType,
	Transaction,
	TransactionStatus,
	TransactionType,
} from '../../../../@types';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { Amount } from '../../../ui/Amount/Amount';
import { ChipStatusTraceability } from '../../../ui/ChipStatusTraceability/ChipStatusTraceability';
import { IconCircleTransaction } from '../../../ui/IconCircleTransaction/IconCircleTransaction';
import { TableHeader } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';
import { UserAvatarModified } from '../../../ui/UserAvatarModified/UserAvatarModified';

// utils
import { translateTransactionType } from '../../../../utils/translations';

// props
export interface GetTransactionsHeadersParams {
	entity: Entity;
	t: TFunction<'translation', undefined>;
}

export const getTransactionsHeaders = ({
	entity,
	t,
}: GetTransactionsHeadersParams): TableHeader[] => {
	const columns: TableHeader[] = [
		{
			label: t('type'),
			value: 'type',
			render: (type: TransactionType, header: TableHeader, transaction: Transaction) => {
				return (
					<Grid alignItems="center" container wrap="nowrap" spacing={1}>
						<Grid item>
							<IconCircleTransaction size="sm" transaction={transaction} />
						</Grid>
						<Grid flex={1} item>
							<Typography fontWeight={600}>{translateTransactionType(type, t)}</Typography>
						</Grid>
					</Grid>
				);
			},
		},
		{
			label: t('date'),
			value: 'date',
			render: (date: Date, header: TableHeader, transaction: Transaction) => {
				return <Time date={new Date(date || transaction.created)} />;
			},
		},
		{
			label: t('expenseType'),
			value: 'expenseType',
			render: (expenseType: ExpenseType) => {
				return <Typography>{expenseType ? expenseType.name : t('na')}</Typography>;
			},
		},
		{
			label: t('evidenced'),
			value: 'evidenced',
			render: (status: TransactionStatus, header: TableHeader, transaction: Transaction) => {
				return <ChipStatusTraceability status={status} traceability={transaction.traceability} />;
			},
		},
		{
			label: t('sent'),
			value: 'total',
			render: (total: number, header: TableHeader, transaction: Transaction) => {
				if (transaction.fromEntity === entity.id) {
					return <Amount currency={transaction.currency} value={total} />;
				} else {
					return '-';
				}
			},
		},
		{
			label: t('received'),
			value: 'total',
			render: (total: number, header: TableHeader, transaction: Transaction) => {
				if (transaction.toEntity === entity.id) {
					return <Amount currency={transaction.currency} value={total} />;
				} else {
					return '-';
				}
			},
		},
		{
			label: t('updated'),
			value: 'modified',
			render: (modified: string, header: TableHeader, transaction: Transaction) => {
				return (
					<UserAvatarModified
						transaction={transaction}
						activityTypes={['TRANSACTION_UPDATED', 'TRANSACTION_CREATED']}
					/>
				);
			},
		},
	];

	return columns;
};
